import React, { useContext, useState, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import {
  Space,
  ColorPicker,
  Row,
  Col,
  Collapse,
  Button,
  List,
  Avatar,
  message,
  Tabs,
  Input,
  Popconfirm,
  Typography,
  Divider,
} from 'antd';
import { useIntl } from 'react-intl';
import {
  CloseOutlined,
  CheckOutlined,
  UserAddOutlined,
  RetweetOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { StoreContext } from '../store/index';
import { updateShift, addFriend } from '../shifts/api';
import { updateUser, fetchUserByEmail } from '../profile/api';
import { rejectRequest } from '../requests/api';
import { isPlayerConfirmedOrPending } from '../shifts/utils';
import RequestModal from './requestModal';
import TokenPaymentModal from './tokenPaymentModal';
import BallIcon from '../components/ballIcon';
import FootIcon from '../components/footIcon';
import PlayerProfileDrawer from '../profile/playerProfileDrawer';

const Player = ({
  player,
  shift,
  isRequest,
  handleAddFriend,
  handleRejectPlayRequest,
  isLocal,
  onInvite,
  handleConfirmPlayRequest,
  handleSwapTeam,
  handleRemovePlayer,
}) => {
  const { formatMessage } = useIntl();
  const store = useContext(StoreContext);
  const {
    user: [user, setUser, setUpdateUser],
  } = store;

  const [loading, setLoading] = useState(false);
  const [showPlayerProfile, setShowPlayerProfile] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showTokenPaymentModal, setShowTokenPaymentModal] = useState(false);

  const removeAcceptedRequests = () => {
    const request = user.requests.find(req => req.shiftId === shift.id);
    if (request) {
      rejectRequest(request.id);
    }
  };

  const handlePlayRequest = () => {
    if (isPlayerConfirmedOrPending(shift, user.uid)) {
      message.error(formatMessage({ id: 'shifts.requests' }));
    } else if (user.uid === shift.userId) {
      updateShift(
        shift.id,
        {
          players: [
            ...shift.players,
            {
              userId: user.uid,
              name: user.nickname,
              photoUrl: user.photoURL,
              email: user.email,
              age: user.age,
              level: user.level,
              position: user.position,
              isLocal,
              goals: 0,
              absent: false,
              yellowCard: 0,
              redCard: false,
            },
          ],
        },
        setLoading,
      );
    } else {
      updateShift(
        shift.id,
        {
          playRequests: [
            ...shift.playRequests,
            {
              userId: user.uid,
              name: user.nickname,
              photoUrl: user.photoURL,
              email: user.email,
              age: user.age,
              level: user.level,
              position: user.position,
              isLocal,
              goals: 0,
              absent: false,
              yellowCard: 0,
              redCard: false,
            },
          ],
        },
        setLoading,
      );
      removeAcceptedRequests();
    }
  };

  const handlePayWithTokens = () => {
    fetchUserByEmail(player.email, setLoading).then(data => {
      updateUser(
        player.userId,
        {
          tokens: data[0].tokens - shift.price,
        },
        setLoading,
      ).then(() => {
        updateShift(
          shift.id,
          {
            players: shift.players.map(p => {
              if (p.userId === player.userId) {
                return { ...p, payed: true, payment: 'tokens' };
              }
              return { ...p };
            }),
          },
          setLoading,
        ).then(() => {
          message.success('Payment Success');
          setUpdateUser(true);
        });
      });
    });
  };
  const handlePayWithCash = () => {
    updateShift(
      shift.id,
      {
        players: shift.players.map(p => {
          if (p.userId === player.userId) {
            return { ...p, payed: true, payment: 'cash' };
          }
          return { ...p };
        }),
      },
      setLoading,
    ).then(() => {
      message.success('Payment Success');
      setUpdateUser(true);
    });
  };
  const handleUnPay = () => {
    if (!shift.isClosed) {
      if (player.payment === 'tokens') {
        fetchUserByEmail(player.email, setLoading).then(data => {
          updateUser(
            player.userId,
            {
              tokens: data[0].tokens + shift.price,
            },
            setLoading,
          ).then(() => {
            updateShift(
              shift.id,
              {
                players: shift.players.map(p => {
                  if (p.userId === player.userId) {
                    return { ...p, payed: false, payment: 'canceled' };
                  }
                  return { ...p };
                }),
              },
              setLoading,
            ).then(() => {
              message.success('Unpayment Success');
              setUpdateUser(true);
            });
          });
        });
      } else {
        updateShift(
          shift.id,
          {
            players: shift.players.map(p => {
              if (p.userId === player.userId) {
                return { ...p, payed: false, payment: 'canceled' };
              }
              return { ...p };
            }),
          },
          setLoading,
        ).then(() => {
          message.success('Unpayment Success');
          setUpdateUser(true);
        });
      }
    }
  };

  const getBorderColor = () => {
    if (isRequest) {
      return '1px dashed lightblue';
    }
    if (!player) {
      return '1px dashed lightblue';
    }
    if (player.payed) {
      return '1px solid lightgreen';
    }
    if (!player.payed) {
      return '1px solid red';
    }
    return '1px dashed lightblue';
  };

  return (
    <Col
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: '200px',
        borderRadius: '8px',
        border: getBorderColor(),
        paddingBottom: '4px',
      }}
      xs={11}>
      {player ? (
        <>
          <Row justify="center">
            <Typography.Text
              strong
              style={{ textAlign: 'center', display: 'block' }}>
              {player.name}
            </Typography.Text>
          </Row>
          <Row
            justify="center"
            style={{ marginTop: '8px' }}
            onClick={() => setShowPlayerProfile(true)}>
            <Avatar size={100} src={player.photoUrl} />
          </Row>
          <Row justify="center" align="middle" gutter={16}>
            <Typography.Text>{` ${player.position || 0}`}</Typography.Text>
          </Row>
          {player.invitedById !== 0 && player.invitedById !== undefined && (
            <Row justify="center" align="middle" gutter={16}>
              <Typography.Text>{`(Invitado por ${player.invitedByName})`}</Typography.Text>
            </Row>
          )}

          {!shift.isFinished && (
            <Row justify="space-evenly" style={{ marginTop: '4px' }}>
              {isRequest && shift.isReserved && (
                <>
                  {(user.isAdmin ||
                    user.uid === shift.userId ||
                    user.uid === player.userId ||
                    user.uid === player.invitedById) && (
                    <Col>
                      <Popconfirm
                        title={formatMessage({
                          id: 'shifts.actions.reject.request.confirm.title',
                        })}
                        description={formatMessage({
                          id: 'common.actions.confirm.question',
                        })}
                        onConfirm={() => handleRejectPlayRequest(player)}
                        onCancel={() => {}}
                        okText={formatMessage({ id: 'common.yes' })}
                        cancelText={formatMessage({ id: 'common.no' })}>
                        <Button
                          danger
                          loading={loading}
                          icon={<CloseOutlined />}
                          ghost
                          type="primary"
                        />
                      </Popconfirm>
                    </Col>
                  )}
                  {(user.isAdmin || user.uid === shift.userId) && (
                    <Col>
                      <Popconfirm
                        title={formatMessage({
                          id: 'shifts.actions.confirm.request.confirm.title',
                        })}
                        description={formatMessage({
                          id: 'common.actions.confirm.question',
                        })}
                        onConfirm={() =>
                          handleConfirmPlayRequest({
                            ...player,
                            isLocal,
                          })
                        }
                        onCancel={() => {}}
                        okText={formatMessage({ id: 'common.yes' })}
                        cancelText={formatMessage({ id: 'common.no' })}>
                        <Button
                          icon={<CheckOutlined />}
                          ghost
                          type="primary"
                          loading={loading}
                        />
                      </Popconfirm>
                    </Col>
                  )}
                </>
              )}
              {!isRequest &&
                user.email !== player.email &&
                !user.friendsIds?.includes(player.email) && (
                  <Col>
                    <Button
                      key={player.id}
                      type="primary"
                      loading={loading}
                      icon={<UserAddOutlined />}
                      onClick={() => handleAddFriend(player.email)}
                    />
                  </Col>
                )}
              {!isRequest &&
                (user.isAdmin ||
                  user.uid === shift.userId ||
                  user.uid === player.userId ||
                  user.uid === player.invitedById) && (
                  <>
                    <Col>
                      <Popconfirm
                        title={formatMessage({
                          id: 'shifts.actions.swap.confirm.title',
                        })}
                        description={formatMessage({
                          id: 'common.actions.confirm.question',
                        })}
                        onConfirm={() => handleSwapTeam(player)}
                        onCancel={() => {}}
                        okText={formatMessage({ id: 'common.yes' })}
                        cancelText={formatMessage({ id: 'common.no' })}>
                        <Button
                          icon={<RetweetOutlined />}
                          ghost
                          type="primary"
                          loading={loading}
                        />
                      </Popconfirm>
                    </Col>
                    {!player.payed && (
                      <Col>
                        <Popconfirm
                          title={formatMessage({
                            id: 'shifts.actions.reject.confirm.title',
                          })}
                          description={formatMessage({
                            id: 'common.actions.confirm.question',
                          })}
                          onConfirm={() => handleRemovePlayer(player)}
                          onCancel={() => {}}
                          okText={formatMessage({ id: 'common.yes' })}
                          cancelText={formatMessage({ id: 'common.no' })}>
                          <Button
                            danger
                            icon={<CloseOutlined />}
                            ghost
                            type="primary"
                            loading={loading}
                          />
                        </Popconfirm>
                      </Col>
                    )}
                  </>
                )}
              {!isRequest && user.uid === player.userId && !player.payed && (
                <Col>
                  <Button
                    key={player.id}
                    ghost
                    type="primary"
                    loading={loading}
                    icon={
                      <DollarOutlined
                        style={{ fontSize: '24px', color: 'gold' }}
                      />
                    }
                    onClick={() => setShowTokenPaymentModal(true)}
                  />
                </Col>
              )}
              {!isRequest && user.uid === shift.userId && !player.payed && (
                <Col>
                  <Popconfirm
                    title={formatMessage({
                      id: 'shifts.actions.pay.cash.confirm',
                    })}
                    description={formatMessage({
                      id: 'common.actions.confirm.question',
                    })}
                    onConfirm={handlePayWithCash}
                    onCancel={() => {}}
                    okText={formatMessage({ id: 'common.yes' })}
                    cancelText={formatMessage({ id: 'common.no' })}>
                    <Button
                      icon={
                        <DollarOutlined
                          style={{ fontSize: '24px', color: 'gold' }}
                        />
                      }
                      ghost
                      type="primary"
                      loading={loading}
                    />
                  </Popconfirm>
                </Col>
              )}
              {!isRequest &&
                user.uid === shift.userId &&
                player.payed &&
                !shift.isClosed && (
                  <Col>
                    <Popconfirm
                      title={formatMessage({
                        id: 'shifts.actions.unpay.confirm',
                      })}
                      description={formatMessage({
                        id: 'common.actions.confirm.question',
                      })}
                      onConfirm={handleUnPay}
                      onCancel={() => {}}
                      okText={formatMessage({ id: 'common.yes' })}
                      cancelText={formatMessage({ id: 'common.no' })}>
                      <Button
                        icon={
                          <DollarOutlined
                            style={{ fontSize: '24px', color: 'gold' }}
                          />
                        }
                        danger
                        loading={loading}
                      />
                    </Popconfirm>
                  </Col>
                )}
            </Row>
          )}
        </>
      ) : (
        <Col>
          {!shift.isFinished && !isPlayerConfirmedOrPending(shift, user.uid) && (
            <Row
              justify="center"
              align="bottom"
              style={{ display: 'flex', alignItems: 'end' }}>
              <Button
                style={{ marginTop: '2px', width: '80%' }}
                type="primary"
                key="primary"
                loading={loading}
                onClick={() => {
                  setShowRequestModal(true);
                }}>
                {formatMessage({ id: 'shifts.actions.play' })}
              </Button>
            </Row>
          )}
          {!isRequest && !shift.isFinished && (
            <Row
              justify="center"
              align="bottom"
              style={{ display: 'flex', alignItems: 'end' }}>
              <Button
                style={{ marginTop: '4px', width: '80%' }}
                type="primary"
                key="primary"
                loading={loading}
                onClick={() => onInvite()}>
                {formatMessage({ id: 'shifts.actions.invite' })}
              </Button>
            </Row>
          )}
        </Col>
      )}
      {showRequestModal && (
        <RequestModal
          shift={shift}
          onConfirm={handlePlayRequest}
          visible={showRequestModal}
          onClose={() => setShowRequestModal(false)}
        />
      )}
      {showTokenPaymentModal && (
        <TokenPaymentModal
          shift={shift}
          playerEmail={player.email}
          onConfirm={handlePayWithTokens}
          visible={showTokenPaymentModal}
          onClose={() => setShowTokenPaymentModal(false)}
        />
      )}
      {showPlayerProfile && (
        <PlayerProfileDrawer
          playerId={player.userId}
          visible={showPlayerProfile}
          onClose={() => setShowPlayerProfile(false)}
        />
      )}
    </Col>
  );
};

export default Player;
